import type { ComponentType } from "react";
import { getCustomerMaster, deleteCustomer } from "~/services/supervisor.services";
import CustomerModal from "./modals/addEditModal";

export type FilterOperator = "=" | "ILIKE";
export type FieldType = "text" | "number" | "select";

export interface FieldOption {
  label: string;
  value: string | number;
}

export interface ReportColumnConfig {
  key: string;
  header: string;
  filterOperator: FilterOperator;


  showInForm?: boolean;
  fieldType?: FieldType;
  required?: boolean;
  maxLength?: number;
  pattern?: RegExp;
  patternMessage?: string;
  options?: FieldOption[];
  apiKeys?: string[];
  alwaysDisabled?: boolean;
  disabledInEdit?: boolean;
  defaultValue?: string | number;
}

export type ReportFetchParams = {
  searchstring: string;
  orderbycol: string;
  orderdirection: "ASC" | "DESC";
  startrowindex: number;
  maximumrows: number;
  gettotal: boolean;
};

export interface ReportConfig {
  key: string;                 // unique id, used as route/lookup key
  title: string;                // page heading
  apiKey: string;               // key to map to API_MAP, SAVE_MAP, CREATE_MAP, DELETE_MAP
  columns: ReportColumnConfig[];
  fetchFn: (params: ReportFetchParams) => Promise<any>;
  deleteFn?: (ids: string[]) => Promise<any>;
  ModalComponent?: ComponentType<any>; // add/edit modal, optional
  exportFileName?: string;
}

export const BOOLEAN_OPTIONS: FieldOption[] = [
  { label: "Yes", value: "true" },
  { label: "No", value: "false" },
];

/**
 * Shared column set for the "customer master" style reports (shipper,
 * consignee, ...). Both masters currently use the exact same shape, so
 * it's defined once here and reused — adding a future master with the
 * same shape is a one-line addition to MASTER_CONFIGS below.
 */
const CUSTOMER_MASTER_COLUMNS: ReportColumnConfig[] = [
  { key: "id", header: "Id", filterOperator: "=", alwaysDisabled: true },
  {
    key: "lookup_key",
    header: "Lookup Key",
    filterOperator: "ILIKE",
    maxLength: 2000,
    apiKeys: ["Lookup_Key"],
    alwaysDisabled: true, // settable on create, locked once a record exists
  },
  { key: "merge_name", header: "Merge Name", filterOperator: "ILIKE", required: true, maxLength: 100, apiKeys: ["Merge_Name"] },
  { key: "merge_address", header: "Merge Address", filterOperator: "ILIKE", required: true, maxLength: 100, apiKeys: ["Merge_Address"] },
  { key: "merge_city", header: "Merge City", filterOperator: "ILIKE", required: true, maxLength: 50, apiKeys: ["Merge_City"] },
  { key: "merge_zip", header: "Merge Zip", filterOperator: "=", fieldType: "number", required: true, maxLength: 10, apiKeys: ["Merge_Zip"] },
  { key: "usersaved_taskid", header: "User Saved Task ID", filterOperator: "=", fieldType: "number", maxLength: 7, apiKeys: ["UserSaved_TaskId"] },
  { key: "usersaved_taskheaderid", header: "User Saved Task Header ID", filterOperator: "=", fieldType: "number", maxLength: 7, apiKeys: ["UserSaved_TaskHeaderId"] },
  { key: "usersaved_account_code", header: "User Saved Account Code", filterOperator: "ILIKE", maxLength: 50, apiKeys: ["UserSaved_AccountCode"] },
  { key: "usersaved_name", header: "User Saved Name", filterOperator: "ILIKE", maxLength: 100, apiKeys: ["UserSaved_Name"] },
  { key: "usersaved_address", header: "User Saved Address", filterOperator: "ILIKE", maxLength: 100, apiKeys: ["UserSaved_Address"] },
  { key: "usersaved_address2", header: "User Saved Address 2", filterOperator: "ILIKE", maxLength: 100, apiKeys: ["UserSaved_Address2"] },
  { key: "usersaved_address3", header: "User Saved Address 3", filterOperator: "ILIKE", maxLength: 100, apiKeys: ["UserSaved_Address3"] },
  { key: "usersaved_city", header: "User Saved City", filterOperator: "ILIKE", maxLength: 50, apiKeys: ["UserSaved_City"] },
  { key: "usersaved_state", header: "User Saved State", filterOperator: "ILIKE", maxLength: 50, apiKeys: ["UserSaved_State"] },
  { key: "usersaved_zip", header: "User Saved Zip", filterOperator: "ILIKE", maxLength: 10, apiKeys: ["UserSaved_Zip"] },
  { key: "usersaved_phone", header: "User Saved Phone", filterOperator: "ILIKE", fieldType: "number", maxLength: 14, apiKeys: ["UserSaved_Phone"] },
  { key: "usersaved_phoneextension", header: "User Saved Phone Extension", filterOperator: "ILIKE", maxLength: 10, apiKeys: ["UserSaved_PhoneExtension"] },
  { key: "is_valid", header: "Is Valid", required: true, filterOperator: "=", fieldType: "select", maxLength: 5, defaultValue: "true", options: BOOLEAN_OPTIONS, apiKeys: ["Is_Valid"] },
  { key: "match_count", header: "Match Count", filterOperator: "=", fieldType: "number", maxLength: 7, apiKeys: ["Match_Count"] },
  { key: "created_date", header: "Created Date", filterOperator: "=", showInForm: false },
  { key: "last_seen_date", header: "Last Seen Date", filterOperator: "=", showInForm: false },
  { key: "is_active", header: "Is Active", required: true, filterOperator: "=", fieldType: "select", maxLength: 5, defaultValue: "true", options: BOOLEAN_OPTIONS, apiKeys: ["Is_Active"] },
  { key: "updatedby", header: "Updated By", filterOperator: "=", showInForm: false },
  { key: "updateddate", header: "Updated Date", filterOperator: "=", showInForm: false },
  { key: "createdby", header: "Created By", filterOperator: "=", showInForm: false },
  { key: "dominant_account_code", header: "Dominant Account Code", filterOperator: "ILIKE", maxLength: 50, apiKeys: ["Dominant_Account_Code"] },
  { key: "dominant_account_count", header: "Dominant Account Count", required: true, filterOperator: "=", fieldType: "number", maxLength: 7, apiKeys: ["Dominant_Account_Count"] },
  {
    key: "dominant_account_percentage", header: "Dominant Account Percentage", required: true, filterOperator: "ILIKE", apiKeys: ["Dominant_Account_Percentage"],
    pattern: /^\d{1,3}(\.\d{1,2})?$/, patternMessage: "Enter numeric value between 0 and 999.99."
  },
  {
    key: "validation_threshold", header: "Validation Threshold", required: true, filterOperator: "ILIKE", apiKeys: ["Validation_Threshold"],
    pattern: /^\d{1,3}(\.\d{1,2})?$/, patternMessage: "Enter numeric value between 0 and 999.99."
  },
  { key: "minimum_match_count", header: "Minimum Match Count", required: true, filterOperator: "=", fieldType: "number", maxLength: 7, apiKeys: ["Minimum_Match_Count"] },
];

export const MASTER_CONFIGS: Record<string, ReportConfig> = {
  shipper: {
    key: "historical_master_shipper",
    title: "Historical Master Shipper",
    apiKey: "historical_master_shipper",
    columns: CUSTOMER_MASTER_COLUMNS,
    fetchFn: (params) => getCustomerMaster(params, "historical_master_shipper"),
    deleteFn: (() => Promise.resolve({ status: false })), // deleteCustomer,
    ModalComponent: CustomerModal,
    exportFileName: "Historical Shipper.xlsx",
  },
  consignee: {
    key: "historical_master_consignee",
    title: "Historical Master Consignee",
    apiKey: "historical_master_consignee",
    columns: CUSTOMER_MASTER_COLUMNS,
    fetchFn: (params) => getCustomerMaster(params, "historical_master_consignee"),
    deleteFn: deleteCustomer,
    ModalComponent: CustomerModal,
    exportFileName: "Historical Consignee.xlsx",
  },

  // some_other_report: {
  //   key: "some_other_report",
  //   title: "Some Other Report",
  //   apiKey: "some_other_report",
  //   columns: [...],
  //   fetchFn: getSomeOtherReport,
  //   deleteFn: deleteSomeOtherRow,   // optional
  //   ModalComponent: SomeOtherModal, // optional — omit to use the generic modal
  // },
};
